import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const TYPES = ["horse", "rental", "job", "service", "tack", "boarding"];
const TACK_TYPES = ["saddle", "bridle", "halter", "blanket", "boots", "other"];
const CONDITIONS = ["new", "like_new", "good", "used"];
const BOARDING_TYPES = ["full_stall", "paddock", "pasture", "self_care"];
const RENTAL_TYPES = ["weekly", "half_lease", "full_lease", "care_share", "other"];
const GENDERS_LIST = ["Mare", "Stallion", "Gelding"];

export default function ListingForm() {
  const { t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [type, setType] = useState("horse");
  const [form, setForm] = useState({
    title: "", description: "", price: "", currency: "EUR",
    location: user?.location || "", category: "",
    breed: "", age: "", gender: "", discipline: "", horse_category: "",
    tack_type: "", condition: "", size: "",
    boarding_type: "", capacity: "", amenities: "",
    rental_type: "",
    contact_email: user?.email || "", contact_phone: "",
  });
  const [images, setImages] = useState([]); // {id, url}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (!isEdit) return;
    api.get(`/listings/${editId}`).then(({ data }) => {
      if (data.user_id !== user.id) { nav("/"); return; }
      setType(data.type);
      setForm({
        title: data.title || "",
        description: data.description || "",
        price: data.price != null ? String(data.price) : "",
        currency: data.currency || "EUR",
        location: data.location || "",
        category: data.category || "",
        breed: data.breed || "",
        age: data.age != null ? String(data.age) : "",
        gender: data.gender || "",
        discipline: data.discipline || "",
        horse_category: data.horse_category || "",
        tack_type: data.tack_type || "",
        condition: data.condition || "",
        size: data.size || "",
        boarding_type: data.boarding_type || "",
        capacity: data.capacity != null ? String(data.capacity) : "",
        amenities: data.amenities || "",
        rental_type: data.rental_type || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
      });
      setImages((data.image_ids || []).map(id => ({ id, url: `${API}/files/${id}` })));
      setLoaded(true);
    }).catch(() => { toast.error(t("error_generic")); nav("/dashboard"); });
  }, [editId, isEdit, user, authLoading, nav, t]);

  if (authLoading || !user || !loaded) return null;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { id: data.id, url: `${API}${data.url}` }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        type,
        title: form.title,
        description: form.description,
        price: form.price ? Number(form.price) : null,
        currency: form.currency,
        location: form.location,
        category: form.category || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        image_ids: images.map(i => i.id),
        breed: (type === "horse" || type === "rental") ? form.breed || null : null,
        age: (type === "horse" || type === "rental") && form.age ? Number(form.age) : null,
        gender: (type === "horse" || type === "rental") ? form.gender || null : null,
        discipline: (type === "horse" || type === "rental") ? form.discipline || null : null,
        horse_category: (type === "horse" || type === "rental") ? form.horse_category || null : null,
        tack_type: type === "tack" ? form.tack_type || null : null,
        condition: type === "tack" ? form.condition || null : null,
        size: type === "tack" ? form.size || null : null,
        boarding_type: type === "boarding" ? form.boarding_type || null : null,
        capacity: type === "boarding" && form.capacity ? Number(form.capacity) : null,
        amenities: type === "boarding" ? form.amenities || null : null,
        rental_type: type === "rental" ? form.rental_type || null : null,
      };
      const { data } = isEdit
        ? await api.patch(`/listings/${editId}`, payload)
        : await api.post("/listings", payload);
      toast.success(isEdit ? t("listing_updated") : t("listing_created"));
      nav(`/listings/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const heading = isEdit ? t("edit_listing") : t("create_listing");

  return (
    <div data-testid={isEdit ? "edit-listing-page" : "new-listing-page"} className="pt-28 pb-20 max-w-3xl mx-auto px-6">
      <div className="overline text-[#143122]">{heading}</div>
      <h1 className="mt-2 font-serif-display text-4xl text-[#143122]">{heading}</h1>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {/* Type */}
        <div>
          <label className="block text-sm font-medium mb-3">{t("listing_type")}</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TYPES.map((tp) => (
              <div
                key={tp}
                data-testid={`type-${tp}`}
                data-selected={type === tp}
                onClick={() => !isEdit && setType(tp)}
                className={`kav-role-card text-center ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <div className="font-serif-display text-lg md:text-xl">{t(`type_label_${tp}`)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">{t("title")}</label>
          <input data-testid="listing-title" required className="kav-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">{t("description")}</label>
          <textarea data-testid="listing-description" required rows={5} className="kav-input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("price")}</label>
            <input data-testid="listing-price" type="number" min="0" step="0.01" className="kav-input" value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("currency")}</label>
            <select data-testid="listing-currency" className="kav-input" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
              <option value="EUR">EUR</option>
              <option value="SEK">SEK</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("location")}</label>
            <input data-testid="listing-location" required className="kav-input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
          </div>
        </div>

        {(type === "horse" || type === "rental") && (
          <>
            {type === "rental" && (
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("rental_type")}</label>
                <div className="flex flex-wrap gap-2">
                  {RENTAL_TYPES.map((rt) => (
                    <button
                      key={rt}
                      type="button"
                      data-testid={`listing-rental-${rt}`}
                      onClick={() => setForm({ ...form, rental_type: form.rental_type === rt ? "" : rt })}
                      className={`kav-chip text-sm ${form.rental_type === rt ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                    >
                      {t(`rental_${rt}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("horse_category")}</label>
              <div className="flex flex-wrap gap-2">
                {["pony", "horse", "foal", "yearling"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`listing-hc-${c}`}
                    onClick={() => setForm({ ...form, horse_category: form.horse_category === c ? "" : c })}
                    className={`kav-chip text-sm ${form.horse_category === c ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                  >
                    {t(`hc_${c}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("gender")}</label>
              <div className="flex flex-wrap gap-2">
                {GENDERS_LIST.map((g) => (
                  <button
                    key={g}
                    type="button"
                    data-testid={`listing-gender-${g.toLowerCase()}`}
                    onClick={() => setForm({ ...form, gender: form.gender === g ? "" : g })}
                    className={`kav-chip text-sm ${form.gender === g ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("breed")}</label>
                <input data-testid="listing-breed" className="kav-input" value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("age")}</label>
                <input data-testid="listing-age" type="number" min="0" className="kav-input" value={form.age} onChange={e => setForm({...form, age: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("discipline")}</label>
                <input data-testid="listing-discipline" className="kav-input" value={form.discipline} onChange={e => setForm({...form, discipline: e.target.value})} />
              </div>
            </div>
          </>
        )}

        {(type === "job" || type === "service") && (
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("category")}</label>
            <input data-testid="listing-category" className="kav-input" placeholder={type === "job" ? "Groom, Rider, Farm hand..." : "Farrier, Vet, Trainer, Transport..."} value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
          </div>
        )}

        {type === "tack" && (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("tack_type")}</label>
              <select data-testid="listing-tack-type" className="kav-input" value={form.tack_type} onChange={e => setForm({...form, tack_type: e.target.value})}>
                <option value="">—</option>
                {TACK_TYPES.map(v => <option key={v} value={v}>{t(`tack_${v}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("condition")}</label>
              <select data-testid="listing-condition" className="kav-input" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})}>
                <option value="">—</option>
                {CONDITIONS.map(v => <option key={v} value={v}>{t(`condition_${v}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("size")}</label>
              <input data-testid="listing-size" className="kav-input" placeholder="17.5″, cob, full..." value={form.size} onChange={e => setForm({...form, size: e.target.value})} />
            </div>
          </div>
        )}

        {type === "boarding" && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("boarding_type")}</label>
                <select data-testid="listing-boarding-type" className="kav-input" value={form.boarding_type} onChange={e => setForm({...form, boarding_type: e.target.value})}>
                  <option value="">—</option>
                  {BOARDING_TYPES.map(v => <option key={v} value={v}>{t(`boarding_${v}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("capacity")}</label>
                <input data-testid="listing-capacity" type="number" min="1" className="kav-input" placeholder="1, 3, 10..." value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("category")}</label>
                <input data-testid="listing-category" className="kav-input" placeholder="Show barn, hobby stable..." value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("amenities")}</label>
              <input data-testid="listing-amenities" className="kav-input" placeholder="Indoor arena, wash bay, trails..." value={form.amenities} onChange={e => setForm({...form, amenities: e.target.value})} />
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("contact_email")}</label>
            <input data-testid="listing-contact-email" type="email" className="kav-input" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("contact_phone")}</label>
            <input data-testid="listing-contact-phone" className="kav-input" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} />
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium mb-3">{t("photos")}</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((img) => (
              <div key={img.id} className="relative aspect-square rounded-md overflow-hidden border border-[#E6E2D8]">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  data-testid={`remove-image-${img.id}`}
                  onClick={() => setImages(images.filter(i => i.id !== img.id))}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white"
                >
                  <X size={14} strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <label data-testid="upload-photos-btn" className="aspect-square border border-dashed border-[#E6E2D8] rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-[#143122] text-[#1A1F1C]/60 hover:text-[#143122] transition-colors">
              <Upload size={20} strokeWidth={1.5} />
              <span className="text-xs mt-2">{uploading ? "..." : t("upload_photos")}</span>
              <input type="file" accept="image/*" multiple hidden onChange={upload} />
            </label>
          </div>
        </div>

        <button data-testid="publish-btn" disabled={busy} type="submit" className="kav-btn-primary w-full justify-center">
          {busy ? "..." : (isEdit ? t("save_changes") : t("publish"))}
        </button>
      </form>
    </div>
  );
}
